import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function ReferenceMap({
  mapData,
  problemCounts,
  selectedLevel,
  selectedCell,
  selectedProblems,
  onLevelChange,
  onCellClick,
  isMobileView = false,
  hasSearchTerm = false,
  searchTerm = ''
}) {
  const scrollContainerRef = useRef(null);

  // selectedCell 변경 시 해당 셀로 자동 스크롤 (가운데 정렬, 바운스 방지)
  useEffect(() => {
    if (selectedCell && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cellId = `${selectedCell.address}-${selectedCell.step}`;
      const target = container.querySelector(`[data-cell-id="${cellId}"]`);
      if (target) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const targetTopInContainer = targetRect.top - containerRect.top + container.scrollTop;
        const idealScrollTop = targetTopInContainer - (container.clientHeight / 2) + (targetRect.height / 2);
        const maxScroll = container.scrollHeight - container.clientHeight;
        const clampedScrollTop = Math.max(0, Math.min(idealScrollTop, maxScroll));
        container.scrollTo({ top: clampedScrollTop, behavior: 'smooth' });
      }
    }
  }, [selectedCell]);

  // 레벨별 표시 텍스트
  const levelTitles = {
    'd1': '대단원',
    'd2': '소단원',
    'd3': '대유형',
    'd4': '중유형'
  };

  // 레벨 순서 정의
  const levelOrder = ['d1', 'd2', 'd3', 'd4'];

  // 현재 레벨의 인덱스 찾기
  const getCurrentLevelIndex = () => {
    return levelOrder.indexOf(selectedLevel);
  };

  // 이전/다음 레벨로 이동하는 함수
  const handlePrevLevel = () => {
    const currentIndex = getCurrentLevelIndex();
    if (currentIndex > 0) {
      onLevelChange(levelOrder[currentIndex - 1]);
    }
  };

  const handleNextLevel = () => {
    const currentIndex = getCurrentLevelIndex();
    if (currentIndex < levelOrder.length - 1) {
      onLevelChange(levelOrder[currentIndex + 1]);
    }
  };

  // 레벨별 폰트 크기 설정 통일
  const levelFontSizes = {
    'd1': 'text-sm font-medium',
    'd2': 'text-sm font-medium',
    'd3': 'text-sm font-medium',
    'd4': 'text-sm font-medium'
  };

  // 행별 문제 수 합계 계산 함수 (검색 필터링용)
  const getRowTotalCount = (itemKey) => {
    if (!problemCounts || !problemCounts.field_problems) return 0;

    const countMap = {
      'd1': problemCounts.field_problems.d1_y_axis || {},
      'd2': problemCounts.field_problems.d2_y_axis || {},
      'd3': problemCounts.field_problems.d3_y_axis || {},
      'd4': problemCounts.field_problems.d4_y_axis || {}
    };

    const levelCounts = countMap[selectedLevel];
    if (!levelCounts || !levelCounts[itemKey]) return 0;

    // Lv1~Lv5 문제 수 합계 반환
    return levelCounts[itemKey].reduce((sum, count) => sum + (count || 0), 0);
  };

  // 검색어로 항목 이름 매칭 (콤마 구분 키워드 지원)
  const matchesSearchTerm = (name) => {
    if (!searchTerm) return false;
    const keywords = searchTerm.split(',').map(k => k.trim()).filter(k => k);
    return keywords.some(keyword => name.includes(keyword));
  };

  // 이름에서 검색 키워드를 노란색으로 하이라이트
  const highlightName = (name) => {
    if (!searchTerm) return name;
    const keywords = searchTerm.split(',').map(k => k.trim()).filter(k => k);
    if (keywords.length === 0) return name;

    const escaped = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'g');
    const parts = name.split(regex);

    if (parts.length === 1) return name;

    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ backgroundColor: 'rgba(250, 204, 21, 0.3)', borderRadius: '2px', padding: '0 1px' }}>{part}</mark>
        : part
    );
  };

  // 데이터 추출 함수
  const getLevelData = () => {
    if (!mapData) return [];

    const levelMap = {
      'd1': mapData.depth1?.['Y-axis'] || [],
      'd2': mapData.depth2?.['Y-axis'] || [],
      'd3': mapData.depth3?.['Y-axis'] || [],
      'd4': mapData.depth4?.['Y-axis'] || []
    };

    const currentLevelData = levelMap[selectedLevel];
    if (!currentLevelData || currentLevelData.length === 0) return [];

    return currentLevelData.map(item => ({
      key: item[2], // 주소 정보 (예: "16/1/1/1")
      name: item[1], // 이름
      status: [0, 1, 2, 3, 4] // 5개의 레벨 위치 인덱스
    }));
  };

  // 셀 클릭 핸들러
  const handleCellClick = (address, step, name) => {
    onCellClick && onCellClick({
      address,  // 예: "16/1/1/1"
      step,     // 1부터 5까지 (lv1~lv5)
      name      // 항목 이름 (예: "수와 연산")
    });
  };
  
  // 현재 셀에서 선택된 문제가 있는지 확인하는 함수
  const hasProblemSelected = (address, level) => {
    if (!selectedProblems || selectedProblems.length === 0) return false;
    
    // 주소와 레벨에 맞는 문제가 선택되었는지 확인
    return selectedProblems.some(problem => 
      problem.address === address && problem.step === (level + 1)
    );
  };
  
  // 상태 박스 렌더링 함수 - 문제 개수 표시
  const renderStatusBox = (item, levelIndex) => {
    const key = item.key;
    const name = item.name;
    
    // 문제 수 데이터 가져오기
    let count = 0;
    if (problemCounts && problemCounts.field_problems) {
      const countMap = {
        'd1': problemCounts.field_problems.d1_y_axis || {},
        'd2': problemCounts.field_problems.d2_y_axis || {},
        'd3': problemCounts.field_problems.d3_y_axis || {},
        'd4': problemCounts.field_problems.d4_y_axis || {}
      };
      
      const levelCounts = countMap[selectedLevel];
      if (levelCounts && levelCounts[key]) {
        count = levelCounts[key][levelIndex] || 0;
      }
    }
    
    // 현재 셀이 선택된 셀인지 확인
    const isSelected = selectedCell && 
                       selectedCell.address === key && 
                       selectedCell.step === (levelIndex + 1);
                       
    // 현재 셀에서 선택된 문제가 있는지 확인
    const hasProblem = hasProblemSelected(key, levelIndex);
    
    // 검색 모드에서 결과가 있는 셀인지 확인
    const hasSearchResult = hasSearchTerm && count > 0;
    
    // 스타일 객체 생성
    const cellStyle = {
      backgroundColor: '#4ABC85',
      opacity: hasProblem ? 1 : 0.4
    };
    
    // 검색 결과가 있는 경우 빨간색 음영 효과 추가
    if (hasSearchResult) {
      cellStyle.boxShadow = '0 0 8px 2px rgba(239, 68, 68, 0.8)';
    }
    
    // 선택된 셀인 경우 검정색 외곽선 추가
    if (isSelected) {
      cellStyle.outline = '2px solid black';
      cellStyle.outlineOffset = '0px';
    }
    
    return (
      <div
        className="flex size-6 cursor-pointer items-center justify-center rounded-3xl hover:bg-primary-hover"
        style={cellStyle}
        data-cell-id={`${key}-${levelIndex + 1}`}
        onClick={() => handleCellClick(key, levelIndex + 1, name)}
      >
        <span className="text-xs font-bold text-white">{count}</span>
      </div>
    );
  };

  return (
    <div className="h-full overflow-hidden">
      <div ref={scrollContainerRef} className={`overflow-y-auto ${isMobileView ? 'h-[calc(100%-20px)] pb-8' : 'h-[calc(100%-2rem)]'}`}>
        {isMobileView ? (
          // 모바일: 분리된 레이아웃 (첫 칼럼만 스크롤)
          <div className="flex">
            {/* 스크롤 가능한 첫 번째 칼럼 */}
            <div className="flex-1 overflow-x-auto border-r">
              <table className="border-collapse">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b">
                    <th className="min-w-[120px] px-2 py-1 text-left font-medium">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={handlePrevLevel}
                          disabled={getCurrentLevelIndex() === 0}
                          className="flex size-5 shrink-0 items-center justify-center rounded hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ChevronLeft size={12} />
                        </button>
                        <span className="truncate text-xs" title={levelTitles[selectedLevel]}>
                          {levelTitles[selectedLevel]}
                        </span>
                        <button
                          onClick={handleNextLevel}
                          disabled={getCurrentLevelIndex() === levelOrder.length - 1}
                          className="flex size-5 shrink-0 items-center justify-center rounded hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <ChevronRight size={12} />
                        </button>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getLevelData()
                    .filter(item => !hasSearchTerm || getRowTotalCount(item.key) > 0 || matchesSearchTerm(item.name))
                    .map((item) => (
                    <tr key={item.key} className="border-b hover:bg-gray-50">
                      <td className="min-w-[120px] px-2 py-2">
                        <div
                          className="cursor-pointer text-xs font-medium hover:text-blue-600"
                          title={item.name}
                        >
                          {highlightName(item.name)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 고정된 Lv 칼럼들 */}
            <div className="shrink-0">
              <table className="border-collapse">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr className="border-b">
                    {[1, 2, 3, 4, 5].map(level => (
                      <th key={level} className="w-[32px] border-b p-1 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-[9px] text-gray-500">Lv</span>
                          <span className="text-xs font-medium">{level}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getLevelData()
                    .filter(item => !hasSearchTerm || getRowTotalCount(item.key) > 0 || matchesSearchTerm(item.name))
                    .map((item) => (
                    <tr key={item.key} className="border-b hover:bg-gray-50">
                      {item.status.map((_, levelIndex) => (
                        <td key={levelIndex} className="w-[32px] p-1 text-center">
                          {renderStatusBox(item, levelIndex)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // 데스크톱: 기존 단일 테이블
          <table className="w-full border-collapse table-fixed">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b">
                <th className="px-2 py-1 text-left font-medium" style={{ width: 'calc(100% - 160px)' }}>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handlePrevLevel}
                      disabled={getCurrentLevelIndex() === 0}
                      className="flex size-5 shrink-0 items-center justify-center rounded hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronLeft size={12} />
                    </button>
                    <span className="truncate text-xs" title={levelTitles[selectedLevel]}>
                      {levelTitles[selectedLevel]}
                    </span>
                    <button
                      onClick={handleNextLevel}
                      disabled={getCurrentLevelIndex() === levelOrder.length - 1}
                      className="flex size-5 shrink-0 items-center justify-center rounded hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <ChevronRight size={12} />
                    </button>
                  </div>
                </th>
                {[1, 2, 3, 4, 5].map(level => (
                  <th key={level} className="border-b p-1 text-center" style={{ width: '32px' }}>
                    <div className="flex flex-col items-center">
                      <span className="text-[9px] text-gray-500">Lv</span>
                      <span className="text-xs font-medium">{level}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {getLevelData()
                .filter(item => !hasSearchTerm || getRowTotalCount(item.key) > 0 || matchesSearchTerm(item.name))
                .map((item) => (
                <tr key={item.key} className="border-b hover:bg-gray-50">
                  <td className="px-2 py-2" style={{ width: 'calc(100% - 160px)' }}>
                    <div
                      className="cursor-pointer text-xs font-medium hover:text-blue-600 truncate pr-2"
                      title={item.name}
                    >
                      {highlightName(item.name)}
                    </div>
                  </td>
                  {item.status.map((_, levelIndex) => (
                    <td key={levelIndex} className="p-1 text-center" style={{ width: '32px' }}>
                      {renderStatusBox(item, levelIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ReferenceMap; 